import { create } from 'zustand';
import { clearQueryCache } from '../query-client';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Auth state.
 *
 * Tokens live in `localStorage` rather than an httpOnly cookie because the API
 * is a separate origin behind CloudFront and the mobile apps use the same
 * bearer flow. The mitigations that matter here are the short access-token TTL
 * (15 min), rotating refresh tokens with reuse detection server-side, and a
 * strict CSP — not cookie storage, which XSS defeats just as easily.
 */

export interface SocietyMembership {
  societyId: string;
  societyName: string;
  societySlug: string;
  societyStatus: string;
  logoUrl: string | null;
  brandColor: string;
  city: string;
  /** ISO 3166-1 alpha-2 — the default country for phone fields. */
  country: string;
  roles: string[];
  flatLabel: string | null;
  flatId: string | null;
  isPrimary: boolean;
}

export interface CurrentUser {
  id: string;
  name: string;
  phone?: string;
  email: string | null;
  avatarUrl: string | null;
  locale?: string;
  /** Society users: whether a panel password has been set. */
  hasPassword?: boolean;
  /** Platform users only. */
  role?: string;
  totpEnabled?: boolean;
}

export type SessionKind = 'user' | 'platform';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  kind: SessionKind | null;
  user: CurrentUser | null;
  societies: SocietyMembership[];
  activeSocietyId: string | null;
  roles: string[];
  permissions: string[];
  /** True while a Super Admin is acting as a society user. */
  isImpersonating: boolean;
  impersonationEndsAt: string | null;
  /** Distinguishes "not signed in" from "we have not checked yet". */
  isBootstrapped: boolean;

  setSession: (input: {
    accessToken: string;
    refreshToken?: string;
    kind: SessionKind;
    user: CurrentUser;
    societies?: SocietyMembership[];
    activeSocietyId?: string | null;
    roles?: string[];
    permissions?: string[];
    isImpersonating?: boolean;
    impersonationEndsAt?: string | null;
  }) => void;
  setAccessToken: (token: string) => void;
  updateUser: (patch: Partial<CurrentUser>) => void;
  /**
   * Seeds a refresh token before there is a session.
   *
   * The one caller is the social sign-in landing page, which arrives holding a
   * refresh token and nothing else and exchanges it for a session.
   */
  setRefreshToken: (token: string) => void;
  setActiveSociety: (societyId: string, roles: string[], accessToken: string) => void;
  refresh: () => Promise<boolean>;
  signOut: () => void;
  setBootstrapped: (value: boolean) => void;
  hasRole: (...roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
}

/** Role inheritance, mirroring `ROLE_IMPLIES` in the API's RBAC middleware. */
const ROLE_IMPLIES: Record<string, string[]> = {
  SOCIETY_ADMIN: ['TREASURER', 'SECRETARY', 'COMMITTEE_MEMBER', 'FACILITY_MANAGER', 'RESIDENT'],
  TREASURER: ['COMMITTEE_MEMBER'],
  SECRETARY: ['COMMITTEE_MEMBER'],
  FACILITY_MANAGER: ['COMMITTEE_MEMBER'],
};

function expand(roles: readonly string[]): Set<string> {
  const out = new Set(roles);
  for (const role of roles) {
    for (const implied of ROLE_IMPLIES[role] ?? []) out.add(implied);
  }
  return out;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      kind: null,
      user: null,
      societies: [],
      activeSocietyId: null,
      roles: [],
      permissions: [],
      isImpersonating: false,
      impersonationEndsAt: null,
      isBootstrapped: false,

      setSession: (input) =>
        set({
          accessToken: input.accessToken,
          refreshToken: input.refreshToken ?? get().refreshToken,
          kind: input.kind,
          user: input.user,
          societies: input.societies ?? [],
          activeSocietyId: input.activeSocietyId ?? null,
          roles: input.roles ?? (input.kind === 'platform' && input.user.role ? [input.user.role] : []),
          permissions: input.permissions ?? [],
          isImpersonating: input.isImpersonating ?? false,
          impersonationEndsAt: input.impersonationEndsAt ?? null,
          isBootstrapped: true,
        }),

      setAccessToken: (token) => set({ accessToken: token }),

      /** A profile edit: the person changed, the session did not. */
      updateUser: (patch) => {
        const current = get().user;
        if (current) set({ user: { ...current, ...patch } });
      },

      setActiveSociety: (societyId, roles, accessToken) => {
        // Switching society changes which tenant every cached answer belongs to,
        // so the cache has to go the same way it does on sign-out.
        clearQueryCache();
        set({ activeSocietyId: societyId, roles, accessToken });
      },

      setRefreshToken: (token) => set({ refreshToken: token }),

      /**
       * Rotates the session.
       *
       * Returns false rather than throwing so the API client can decide whether
       * to retry or sign the user out.
       */
      refresh: async () => {
        const token = get().refreshToken;
        if (!token) return false;

        try {
          const response = await fetch(
            `${import.meta.env['VITE_API_BASE_URL'] ?? ''}/api/v1/auth/refresh`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken: token }),
            },
          );

          if (!response.ok) return false;

          const payload = (await response.json()) as {
            success: boolean;
            data?: { tokens: { accessToken: string; refreshToken: string }; kind: SessionKind };
          };
          if (!payload.success || !payload.data) return false;

          set({
            accessToken: payload.data.tokens.accessToken,
            refreshToken: payload.data.tokens.refreshToken,
            kind: payload.data.kind,
          });
          return true;
        } catch {
          return false;
        }
      },

      signOut: () => {
        // Everything cached belongs to the identity that is leaving. Without this,
        // the next person to sign in is served the previous society's members,
        // bills and complaints until each query happens to refetch — a full page
        // reload was the only thing that cleared it.
        clearQueryCache();
        set({
          accessToken: null,
          refreshToken: null,
          kind: null,
          user: null,
          societies: [],
          activeSocietyId: null,
          roles: [],
          permissions: [],
          isImpersonating: false,
          impersonationEndsAt: null,
          isBootstrapped: true,
        });
      },

      setBootstrapped: (value) => set({ isBootstrapped: value }),

      hasRole: (...roles) => {
        const state = get();
        // A platform user is never blocked by a society-role check; their access
        // is gated separately and audited.
        if (state.kind === 'platform') return true;
        const held = expand(state.roles);
        return roles.some((role) => held.has(role));
      },

      hasPermission: (permission) => {
        const state = get();
        if (state.kind === 'platform') return true;
        if (expand(state.roles).has('SOCIETY_ADMIN')) return true;
        const [head] = permission.split('.');
        return state.permissions.includes(permission) || state.permissions.includes(`${head}.*`);
      },
    }),
    {
      name: 'kinshipos.auth',
      storage: createJSONStorage(() => localStorage),
      // `isBootstrapped` is a runtime flag, not session state — persisting it
      // would make a reload skip the `/auth/me` check.
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        kind: state.kind,
        user: state.user,
        societies: state.societies,
        activeSocietyId: state.activeSocietyId,
        roles: state.roles,
        permissions: state.permissions,
        isImpersonating: state.isImpersonating,
        impersonationEndsAt: state.impersonationEndsAt,
      }),
    },
  ),
);

/** Convenience selectors so components subscribe to the narrowest slice. */
export const selectIsAuthenticated = (s: AuthState): boolean => Boolean(s.accessToken && s.user);
export const selectActiveSociety = (s: AuthState): SocietyMembership | undefined =>
  s.societies.find((society) => society.societyId === s.activeSocietyId);
